// API Configuration
// In production, this will be set via environment variable during build
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ai-translation.ai-coe-dev.aws.evernorthcloud.com/v1';

// Temporary: Auth token from environment variable (will be replaced with OKTA OIDC)
const AUTH_TOKEN = process.env.REACT_APP_AUTH_TOKEN || '';

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Add Authorization header if token is available
  if (AUTH_TOKEN) {
    defaultHeaders['Authorization'] = `Bearer ${AUTH_TOKEN}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `API error: ${response.status}`);
  }

  return response.json();
}

// Submit a new translation request
export async function submitTranslation(data) {
  return apiCall('/translate', {
    method: 'POST',
    body: JSON.stringify({
      source: data.source,
      sourceLanguage: data.sourceLanguage || 'en-US',
      targetLanguage: data.targetLanguage || 'es-US',
      category: data.category || 'general',
    }),
  });
}

// Get status of a specific request
export async function getRequestStatus(reqId) {
  return apiCall(`/translate?req_id=${encodeURIComponent(reqId)}`, {
    method: 'GET',
  });
}

// Get all requests (this calls a scan endpoint - may need to be created)
// For now, we'll simulate with localStorage + API polling
export async function getAllRequests() {
  // Get stored request IDs from localStorage
  const storedIds = JSON.parse(localStorage.getItem('translationRequestIds') || '[]');
  
  // Fetch status for each request
  const requests = await Promise.all(
    storedIds.map(async (id) => {
      try {
        return await getRequestStatus(id);
      } catch (error) {
        return {
          req_id: id,
          status: 'unknown',
          error: error.message,
        };
      }
    })
  );

  // Sort by received_date descending (newest first)
  return requests.sort((a, b) => {
    const dateA = new Date(a.received_date || 0);
    const dateB = new Date(b.received_date || 0);
    return dateB - dateA;
  });
}

// Store a request ID locally (called after successful submission)
export function storeRequestId(reqId) {
  const storedIds = JSON.parse(localStorage.getItem('translationRequestIds') || '[]');
  if (!storedIds.includes(reqId)) {
    storedIds.unshift(reqId); // Add to beginning
    // Keep only last 100 requests
    const trimmed = storedIds.slice(0, 100);
    localStorage.setItem('translationRequestIds', JSON.stringify(trimmed));
  }
}

// Save translation to Translation Memory (PUT endpoint)
export async function saveToTranslationMemory(data) {
  return apiCall('/translate', {
    method: 'PUT',
    body: JSON.stringify({
      source: data.source,
      sourceLanguage: data.sourceLanguage || 'en-US',
      target: data.target,
      targetLanguage: data.targetLanguage || 'es-US',
      category: data.category || 'linguist-approved',
      notes: data.notes || null,
    }),
  });
}
